import { ObjectItemType } from 'webstudio/src/client/project_content_types';
import { BASE_API_URL, ELicenceCodes, PROJECT_STATUS } from '_data/constants';
import FormData from '_formdata';
import { isNonNullable } from '_helpers/nullable';
import SelectedProjectMap from './SelectedStorageItemsSet';

export const shallowCompare = (obj1, obj2) =>
  Object.keys(obj1).length === Object.keys(obj2).length &&
  Object.keys(obj1).every(key => isNonNullable(obj2[key]) && obj1[key] === obj2[key]);

export const removeEmpty = obj =>
  Object.entries(obj).reduce(
    (acc, [key, value]) => ({ ...acc, ...(isNonNullable(value) ? { [key]: value } : undefined) }),
    {}
  );

export const getElementBy = (arr, field) => {
  const key = Object.keys(field)[0];
  return field ? arr.find(item => item[key] === field[key]) : undefined;
};

export const debounce = (f, ms = 300) => {
  let timer = null;
  return (...args) => {
    const onComplete = () => {
      f.apply(this, args);
      timer = null;
    };

    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(onComplete, ms);
  };
};

export const isEmpty = value => {
  if (!value) {
    return true;
  }
  if (Array.isArray(value) || typeof value === 'string') {
    return !value.length;
  }

  if (typeof value === 'object') {
    return Object.keys(value).every(key => isEmpty(value[key]));
  }

  return false;
};

export const buildFormData = (formData, data, parentKey) => {
  if (
    data &&
    typeof data === 'object' &&
    !(data instanceof Date) &&
    !(data instanceof File) &&
    !(data instanceof Blob) &&
    !(data instanceof ArrayBuffer)
  ) {
    Object.keys(data).forEach(key => {
      buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
    });
  } else {
    const value = data == null ? '' : data;
    formData.append(parentKey, value);
  }
};

export const jsonToFormData = data => {
  const formData = new FormData();
  buildFormData(formData, data);
  return formData;
};

export const getShortName = name =>
  name
    ? (name.indexOf(' ') !== -1
        ? name
            .split(' ')
            .map(a => a.charAt(0))
            .join('')
        : name
      )
        .substr(0, 2)
        .toUpperCase()
    : '';

export const buf2hex = buffer =>
  Array.prototype.map.call(new Uint8Array(buffer), x => `00${x.toString(16)}`.slice(-2)).join('');

export const getTimeLeftString = (miliseconds, reason, suffix = '', shortHours = false) => {
  if (miliseconds === null) {
    return reason;
  }
  const seconds = Math.floor(miliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  let hoursString;

  if (shortHours) {
    hoursString = 'h';
  } else {
    hoursString = hours > 1 ? 'hours' : 'hour';
  }

  return hours > 0
    ? `${hours}${hoursString} ${minutes - hours * 60}min ${suffix}`
    : minutes > 0
    ? `${minutes} min ${suffix}`
    : seconds >= 0
    ? `${seconds}s ${suffix}`
    : reason;
};

export const getExponent = (x, y) => (y === 0 ? 0 : Math.floor(Math.log(y) / Math.log(x)));

const DIMENSIONS = ['', ' KB', ' MB', ' GB', ' TB', ' PB'];

export const getFileSizeString = (bytes, noDimension) => {
  const exponent = getExponent(1024, bytes);
  const dimension = noDimension ? '' : DIMENSIONS[exponent] || '';
  const size = (bytes / 1024 ** exponent).toFixed(2) / 1;

  return `${size}${dimension}`;
};

export const multipleSelect = (changeStateFunc, selected, item, all, isSwitchMode) => (_, e, add, __, noSwitch) => {
  // switchMode true allow multiselect only with shift
  let _selected;

  if (isSwitchMode) {
    _selected = new SelectedProjectMap();
  } else {
    _selected = new SelectedProjectMap(__ || selected);
  }

  if (add || e.nativeEvent.metaKey || e.targetTouches) {
    if (selected.has(item) && !noSwitch) {
      _selected.delete(item);
    } else {
      _selected.add(item);
    }
  } else if (e.nativeEvent.shiftKey && selected.size > 0) {
    let last = item;
    // eslint-disable-next-line no-restricted-syntax
    for (last of selected);

    const index = all.indexOf(item);
    let lastIndex = all.indexOf(last);

    const diff = index - lastIndex;
    if (!diff) {
      return;
    }
    const _add = diff / Math.abs(diff);

    if (isSwitchMode) _selected.add(last);

    do {
      lastIndex += _add;
      _selected.add(all[lastIndex - _add]);
    } while (lastIndex !== index);

    if (!_selected.delete(item)) {
      _selected.add(item);
    }
  } else if (e.nativeEvent.ctrlKey) {
    if (!_selected.delete(item)) {
      _selected.add(item);
    }
  } else {
    _selected = new SelectedProjectMap([item]);
  }

  changeStateFunc(_selected);
};

export const formatPropertyValue = (value, suffix = '', toFixedNumber = 2) =>
  typeof value !== 'number' && Number.isNaN(Number(value))
    ? value
    : (Number(value) || 0).toFixed(toFixedNumber).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ') + suffix;

/**
 * Find all items in group
 * @param {object} group
 * @param {Set} items
 * @param {number} groupType
 */
export const findInGroup = (group, items, groupType, predicate) => {
  if (group.items && group.items.length > 0) {
    group.items.forEach(item => {
      if (typeof predicate === 'function') {
        if (predicate(item)) {
          items.add(item.id);
        }
      } else {
        items.add(item.id);
      }
      if (item.type === groupType) {
        items = new Set([...items, ...findInGroup(item, items, groupType, predicate)]);
      }
    });
  }
  return items;
};

export const forEachInGroupPlain = (group, allPlain, predicate) => {
  predicate(group);
  allPlain
    .filter(_ => _.groupID === group.id)
    .forEach(__ => {
      forEachInGroupPlain(__, allPlain, predicate);
    });
};

export const findInGroupPlain = (group, allPlain) => {
  const items = allPlain.filter(_ => _.groupID === group.id);
  return items.concat(items.map(_ => findInGroupPlain(_, allPlain)).flat());
};

export const makeGroupsOfObjects = (list, params) => {
  const { excludePredicate, parentPropName, parentColorPropName, notAlignedGroupId } = params;

  const _objectsSorted = list
    .filter(item => !excludePredicate(item))
    .sort((a, b) => {
      if (typeof a[parentPropName] === 'undefined') {
        return 1;
      }
      if (typeof b[parentPropName] === 'undefined') {
        return -1;
      }
      return a[parentPropName] > b[parentPropName] ? 1 : a[parentPropName] < b[parentPropName] ? -1 : 0;
    });

  const _res = _objectsSorted.reduce((acc, cur, index, arr) => {
    if (index === 0 || arr[index - 1][parentPropName] !== cur[parentPropName]) {
      acc.push({
        id: cur[parentPropName] || notAlignedGroupId,
        color: cur[parentColorPropName] || 'none',
        type: ObjectItemType.Group,
        items: [cur],
      });
    } else {
      acc[acc.length - 1].items.push(cur);
    }
    return acc;
  }, []);
  return _res;
};

export const makeGroups = (list, params, excludePredicate) => {
  const { groupPropName, groupPropValue, parentPropName, parentColorPropName, notAlignedGroupName, notAlignedGroupId } =
    params;

  const alignGroups = makeGroupsOfObjects(list, {
    excludePredicate,
    parentPropName,
    parentColorPropName,
    notAlignedGroupId,
  });

  const ungroupedItems = {
    name: notAlignedGroupName,
    id: notAlignedGroupId,
    color: 'none',
    type: ObjectItemType.Group,
  };

  const _list = [...alignGroups, ungroupedItems, ...list];

  const _mappedList = _list.reduce((acc, item) => {
    if (item[groupPropName] === groupPropValue) {
      acc.set(item.id, {
        ...item,
        items: [],
      });
    } else {
      acc.has(item[parentPropName])
        ? acc.get(item[parentPropName]).items.push(item)
        : acc.get(notAlignedGroupId).items.push(item);
    }
    return acc;
  }, new Map());
  return Array.from(_mappedList.values());
};

export const normalizeRGB = ({ r, g, b }) => ({
  r: Math.round(255 * r),
  g: Math.round(255 * g),
  b: Math.round(255 * b),
});

export const rgbStringToRgbObject = rgbString => {
  const [r, g, b, a] = rgbString.replace(/[^\d,]/g, '').split(',');

  return {
    r: parseInt(r, 10),
    g: parseInt(g, 10),
    b: parseInt(b, 10),
    ...(isNonNullable(a) ? { a: parseInt(a, 10) } : undefined),
  };
};

export const hexToRgb = hex => {
  if (!hex) return [1, 1, 1];
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16) / 255, parseInt(result[2], 16) / 255, parseInt(result[3], 16) / 255]
    : [1, 1, 1];
};

export const denormalizeRGB = p => {
  const { r, g, b } = p;

  return {
    r: Math.floor((r * 100) / 255) / 100,
    g: Math.floor((g * 100) / 255) / 100,
    b: Math.floor((b * 100) / 255) / 100,
  };
};

export const getRandomColor = () => ({
  r: Math.floor(Math.random() * (255 + 1)),
  g: Math.floor(Math.random() * (255 + 1)),
  b: Math.floor(Math.random() * (255 + 1)),
});

const rgbToHsl = ({ r, g, b }) => {
  (r /= 255), (g /= 255), (b /= 255);

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h;
  let s;
  const l = (max + min) / 2;

  if (max === min) {
    // eslint-disable-next-line no-multi-assign
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    // eslint-disable-next-line default-case
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return { h, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  let r;
  let g;
  let b;

  if (s === 0) {
    // eslint-disable-next-line no-multi-assign
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
};

export const generateShades = (color, shadesCount) => {
  const { h, s, l } = rgbToHsl(color);
  const newCount = shadesCount + 1;
  const step = (1 - l) / newCount;

  const shadesIterator = (iterations, result) => {
    if (iterations > 0) {
      return shadesIterator(iterations - 1, [...result, hslToRgb({ h, s, l: l + step * iterations })]);
    }
    return result.slice(1).reverse();
  };

  return shadesIterator(newCount, []);
};

export const generateColorInSequence = (iteration, initialHue = 0, s = 0.5, l = 0.5) => {
  const goldenRatioConjugate = 0.618033988749895;
  const currentColor = (initialHue + goldenRatioConjugate * iteration) % 1;
  return hslToRgb({ h: currentColor, s, l });
};

export const getCookie = name => {
  const matches = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`) // eslint-disable-line
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
};

export const setCookie = (name, value, options = {}) => {
  const _options = {
    path: '/',
    ...options,
  };

  if (_options.expires && _options.expires.toUTCString) {
    _options.expires = _options.expires.toUTCString();
  }

  let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  Object.keys(_options).forEach(optionKey => {
    updatedCookie += `; ${optionKey}`;
    const optionValue = _options[optionKey];
    if (optionValue !== true) {
      updatedCookie += `=${optionValue}`;
    }
  });

  document.cookie = updatedCookie;
};

export const objToSelectOptions = obj => Object.entries(obj).map(([key, value]) => ({ label: key, value }));

export const jsonFormat = obj => {
  const text = JSON.stringify(obj);
  return text ? text.replace(/\\/g, '') : '';
};

const componentToHex = c => {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};

export const rgbToHex = ({ r, g, b }) => `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;

export const groupsToOptions = groups =>
  groups.map(group => {
    if (group.id === 'NOT ALIGNED' && group.items.length > 0) {
      return {
        label: group.id,
        options: group.items.map(item => ({
          label: item.name,
          value: [item.id],
        })),
      };
    }
    return {
      label: group.id,
      color: group.color,
      value: group.items.map(item => item.id),
    };
  });

export const isInSubFolder = (folders, folderId, forldersIds) => {
  const parent = folders.find(item => item.id === folderId);
  if (!parent) {
    return false;
  }
  if (forldersIds.indexOf(parent.id) !== -1) {
    return true;
  }
  return isInSubFolder(folders, parent.folder_id, forldersIds);
};

export const getShortScanLabel = _label => (_label.length <= 60 ? _label : `${_label.slice(0, 57)}...`);

export const getKeyFromValue = (obj, val) => {
  const prop = Object.entries(obj).find(_ => _[1] === val);
  return prop ? prop[0] : '';
};

const getPairs = (obj, keys = []) =>
  Object.entries(obj).reduce((pairs, [key, value]) => {
    if (value === null) {
      return pairs;
    }
    if (typeof value === 'object') {
      pairs.push(...getPairs(value, [...keys, key]));
    } else {
      pairs.push([[...keys, key], value]);
    }
    return pairs;
  }, []);

export const jsonToQuery = obj =>
  Object.keys(obj).length
    ? `?${getPairs(obj)
        .map(
          ([[key0, ...keysRest], value]) =>
            `${key0}${keysRest.map(a => `[${a}]`).join('')}=${encodeURIComponent(value)}`
        )
        .join('&')}`
    : '';

export const hexToBinaryString = hex => {
  const bytes = [];
  const { length } = hex;
  let x;

  for (x = 0; x < length - 1; x += 2) {
    bytes.push(parseInt(hex.substr(x, 2), 16));
  }

  // eslint-disable-next-line prefer-spread
  return String.fromCharCode.apply(String, bytes);
};

export const formatSelected = (items, selectedItems, groupType) =>
  items.reduce((acc, cur) => {
    if (cur.type !== groupType) {
      if (selectedItems.indexOf(cur.id) !== -1) {
        acc.push(cur);
      }
    } else {
      const itemsInGroup = [...findInGroup(cur, new Set(), groupType, __ => __.type !== groupType)];
      if (itemsInGroup.length > 0) {
        if (itemsInGroup.every(_ => selectedItems.indexOf(_) !== -1)) {
          acc.push(cur);
        } else {
          const subItems = formatSelected(cur.items, selectedItems, groupType);
          return acc.concat(subItems);
        }
      }
    }

    return acc;
  }, []);

export const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const rndSession = () =>
  'xxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const stringWithSpacesToNumber = string => Number(string.replaceAll(/\s/g, ''));

export const getObjectsMeta = (items, fields = ['name', 'uuid', 'type']) =>
  items.map(item =>
    fields.reduce((acc, field) => {
      acc[field] = item[field];
      return acc;
    }, {})
  );

export const getDownloadAttachmentLink = (projectId, annotationId, attachmentId) =>
  `${BASE_API_URL}/projects/${projectId}/annotations/${annotationId}/attachments/${attachmentId}.json`;

export const getClosest = (needle, haystack) =>
  haystack.reduce((a, b) => {
    const aDiff = Math.abs(a - needle);
    const bDiff = Math.abs(b - needle);

    if (aDiff === bDiff) {
      return a > b ? a : b;
    }
    return bDiff < aDiff ? b : a;
  });

export const intersectArrays = (a, b) => a.filter(_ => b.includes(_));

export const filterTextInputs = e => !!e.target.getAttribute('nohotkeys');

export const uploadingStatus = _status => {
  let text = '';

  switch (_status) {
    case PROJECT_STATUS.preparing:
      text = 'Preparing';
      break;
    case PROJECT_STATUS.new:
    case PROJECT_STATUS.uploading:
      text = 'Uploading';
      break;
    case PROJECT_STATUS.uploaded:
      text = 'Uploaded';
      break;
    case PROJECT_STATUS.processing:
      text = 'Converting';
      break;
    default:
      break;
  }

  return text;
};

const licenseNames = new Map([
  [ELicenceCodes.FREE, 'Free plan'],
  [ELicenceCodes.STORAGE_200GB, '200 GB storage'],
  [ELicenceCodes.STORAGE_200GB_MO, '200 GB storage'],
  [ELicenceCodes.STORAGE_1TB, '1 TB storage'],
  [ELicenceCodes.STORAGE_1TB_MO, '1 TB storage'],
  ['ac_qa_unlimited', 'QA unlimited'],
]);

export const getLicenseNane = code => licenseNames.get(code) || 'Plan';

export const arrayIntersection = (arr1, arr2) => arr1.filter(item1 => arr2.some(item2 => item1.id === item2.id));
